import { useState } from 'react';
import { usePersistedState } from '../use-persisted-state';
import { Panel } from '../whoop-panel';
import { Segmented } from '../segmented-control';
import { ToggleSwitch } from '../toggle-switch';
import { C } from '../whoop-theme';

interface Signals {
  recovery: boolean;
  sleep: boolean;
  hrv: boolean;
  rhr: boolean;
  strain: boolean;
  planned: boolean;
}

interface Settings {
  conservatism: string;
  signals: Signals;
  publicRecap: boolean;
  homeWidgets: boolean;
  goalTitle: string;
  raceDate: string;
  targetTime: string;
  maxRuns: number;
  restDays: string;
}

const DEFAULT_SETTINGS: Settings = {
  conservatism: 'balanced',
  signals: { recovery: true, sleep: true, hrv: true, rhr: true, strain: true, planned: true },
  publicRecap: false,
  homeWidgets: false,
  goalTitle: 'Sub-3:45 marathon',
  raceDate: '2026-10-11',
  targetTime: '3:45:00',
  maxRuns: 5,
  restDays: 'Mon, Fri',
};

const CONNECTION = [
  { k: 'Status', v: 'Connected' },
  { k: 'Last sync', v: '8 minutes ago' },
  { k: 'Data range', v: 'Jan 2025 – present' },
  { k: 'Permissions', v: 'recovery · sleep · cycles · workouts' },
];

const CONS_OPTS = [
  { value: 'conservative', label: 'Conservative' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'aggressive', label: 'Aggressive' },
];

const CONS_DESC: Record<string, string> = {
  conservative: 'Smaller cuts, err toward rest. Best near a race.',
  balanced: 'Trade volume and intensity case by case.',
  aggressive: 'Push planned work unless signals are clearly bad.',
};

const SIGNAL_DEFS: [keyof Signals, string][] = [
  ['recovery', 'Recovery'],
  ['sleep', 'Sleep'],
  ['hrv', 'HRV'],
  ['rhr', 'Resting HR'],
  ['strain', 'Recent strain'],
  ['planned', 'Planned workouts'],
];

export function WhoopSettingsPage() {
  const [settings, setSettings] = usePersistedState<Settings>('settings', DEFAULT_SETTINGS);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  const toggleSignal = (key: keyof Signals) =>
    setSettings({ ...settings, signals: { ...settings.signals, [key]: !settings.signals[key] } });

  const prefs = [
    { k: 'Primary goal', v: settings.goalTitle },
    { k: 'Race date', v: 'Oct 11, 2026' },
    { k: 'Target time', v: settings.targetTime },
    { k: 'Max weekly runs', v: String(settings.maxRuns) },
    { k: 'Preferred rest days', v: settings.restDays },
  ];

  const privacy: { key: 'publicRecap' | 'homeWidgets'; label: string }[] = [
    { key: 'publicRecap', label: 'Allow public share link for yearly recap' },
    { key: 'homeWidgets', label: 'Show selected widgets on site homepage' },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.025em', margin: 0 }}>
          Data &amp; Settings
        </h1>
        <div style={{ fontSize: 14, color: C.text2, marginTop: 4 }}>
          Connection, sync, goals, and how the recommendation engine behaves.
        </div>
      </div>

      {/* Connection */}
      <Panel style={{ padding: '22px 24px', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <span
            style={{
              width: 34,
              height: 34,
              borderRadius: 9,
              background: 'rgba(181,155,255,0.16)',
              color: C.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontFamily: "'JetBrains Mono',monospace",
            }}
          >
            W
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>WHOOP</div>
            <div
              style={{ fontSize: 12, color: C.good, display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.good }} />
              Connected
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))',
            gap: 14,
            marginBottom: 18,
          }}
        >
          {CONNECTION.map((c) => (
            <div key={c.k}>
              <div style={{ fontSize: 11, color: C.text3, marginBottom: 4 }}>{c.k}</div>
              <div style={{ fontSize: '13.5px', fontWeight: 600 }}>{c.v}</div>
            </div>
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 9,
            paddingTop: 16,
            borderTop: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <button
            type="button"
            onClick={() => setSyncMsg('Synced just now — 0 new records')}
            className="ww-btn"
            style={{
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              borderRadius: 9,
              padding: '9px 15px',
              border: 'none',
              background: C.accent,
              color: '#1a1330',
            }}
          >
            Sync now
          </button>
          <button
            type="button"
            onClick={() => setSyncMsg('Re-authenticated with WHOOP.')}
            className="ww-btn"
            style={{
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              borderRadius: 9,
              padding: '9px 15px',
              border: '1px solid rgba(255,255,255,0.14)',
              background: 'rgba(255,255,255,0.05)',
              color: C.text,
            }}
          >
            Reconnect
          </button>
          <button
            type="button"
            className="ww-btn"
            style={{
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              borderRadius: 9,
              padding: '9px 15px',
              border: '1px solid rgba(255,255,255,0.14)',
              background: 'transparent',
              color: C.text2,
            }}
          >
            Disconnect
          </button>
          <button
            type="button"
            className="ww-btn"
            style={{
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              borderRadius: 9,
              padding: '9px 15px',
              border: '1px solid rgba(224,137,122,0.3)',
              background: 'transparent',
              color: '#e0897a',
            }}
          >
            Delete imported data
          </button>
        </div>
        {syncMsg && (
          <div
            style={{
              marginTop: 14,
              padding: '10px 14px',
              background: 'rgba(127,209,163,0.1)',
              border: '1px solid rgba(127,209,163,0.3)',
              borderRadius: 10,
              fontSize: 13,
              color: '#a9e3c4',
            }}
          >
            ✓ {syncMsg}
          </div>
        )}
      </Panel>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>
        {/* Training prefs */}
        <Panel style={{ padding: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>
            Training preferences
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {prefs.map((p) => (
              <div
                key={p.k}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '11px 0',
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <span style={{ fontSize: 13, color: C.text2 }}>{p.k}</span>
                <span style={{ fontSize: '13.5px', fontWeight: 600, textAlign: 'right' }}>
                  {p.v}
                </span>
              </div>
            ))}
          </div>
        </Panel>

        {/* Recommendation prefs */}
        <Panel style={{ padding: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>
            How adaptations behave
          </div>
          <div style={{ marginBottom: 10 }}>
            <Segmented
              options={CONS_OPTS}
              active={settings.conservatism}
              onSelect={(conservatism) => setSettings({ ...settings, conservatism })}
              padY={9}
            />
          </div>
          <div
            style={{
              fontSize: 12,
              color: C.text2,
              lineHeight: 1.45,
              marginBottom: 18,
              minHeight: 34,
            }}
          >
            {CONS_DESC[settings.conservatism]}
          </div>
          <div
            style={{
              fontSize: 11,
              color: C.text3,
              marginBottom: 10,
              fontFamily: "'JetBrains Mono',monospace",
              letterSpacing: '0.08em',
            }}
          >
            SIGNALS THAT INFLUENCE RECOMMENDATIONS
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {SIGNAL_DEFS.map(([key, label]) => (
              <div
                key={key}
                onClick={() => toggleSignal(key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 0',
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 13 }}>{label}</span>
                <ToggleSwitch on={settings.signals[key]} />
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Privacy */}
      <Panel style={{ padding: '22px 24px' }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Privacy</div>
        <div style={{ fontSize: 12, color: C.text3, marginBottom: 16 }}>
          Everything is private by default. These control what can leave the workspace.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {privacy.map((pv) => (
            <div
              key={pv.key}
              onClick={() => setSettings({ ...settings, [pv.key]: !settings[pv.key] })}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '11px 0',
                borderTop: '1px solid rgba(255,255,255,0.06)',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: '13.5px' }}>{pv.label}</span>
              <ToggleSwitch on={settings[pv.key]} />
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
